'use client';

import { StorageKeys } from '@/hotdesk-ui/enum/storage-keys';
import { storeDelete, storeSet } from '@/hotdesk-ui/helpers/utils';
import { createSlice } from '@reduxjs/toolkit';
import { User } from 'hotdesk_typescript';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

export type AuthModalStep =
    | 'email'
    | 'password'
    | 'signup'
    | 'reset'
    | 'verify';

export interface Auth {
    isOpenAuthModal: boolean;
    user: User | null;
    token: string | null;
    refresh: string | null;
    authModalInitialStep: AuthModalStep | null;
    authModalInitialEmail: string | null;
}

// Keep the auth cookies alive across browser restarts (redux auth is persisted
// too, so a session-only cookie would fall out of sync). 30 days in seconds.
const AUTH_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

const initialState: Auth = {
    isOpenAuthModal: false,
    user: null,
    token: null,
    refresh: null,
    authModalInitialStep: null,
    authModalInitialEmail: null
};

export const AuthModalSlice = createSlice({
    name: 'authModal',
    initialState: initialState,
    reducers: {
        setIsOpenAuthModal: (state, action) => {
            state.isOpenAuthModal = action.payload;
            if (!action.payload) {
                state.authModalInitialStep = null;
                state.authModalInitialEmail = null;
            }
        },
        openAuthModalAt: (
            state,
            action: {
                payload: { step: AuthModalStep; email?: string };
                type: string;
            }
        ) => {
            state.isOpenAuthModal = true;
            state.authModalInitialStep = action.payload.step;
            state.authModalInitialEmail = action.payload.email ?? null;
        },
        setUserData: (state, action) => {
            state.user = action.payload;
        },
        setToken: (state, action) => {
            state.token = action.payload;
            // Persist the cookie (30d) so it survives a browser restart, matching
            // the persisted redux auth. A session cookie here left logged-in
            // users' /my-account & /my-bookings (server-read from this cookie)
            // failing to landing until they re-logged in.
            storeSet(StorageKeys.ACCESS_TOKEN, action.payload, {
                maxAge: AUTH_COOKIE_MAX_AGE,
                path: '/'
            });
        },
        setRefreshToken: (state, action) => {
            if (state.user) {
                state.refresh = action.payload;
            }
            storeSet(StorageKeys.REFRESH_TOKEN, action.payload, {
                maxAge: AUTH_COOKIE_MAX_AGE,
                path: '/'
            });
        },
        logout: (state) => {
            state.user = null;
            state.token = null;
            state.isOpenAuthModal = false;
            storeDelete(StorageKeys.ACCESS_TOKEN);
            storeDelete(StorageKeys.REFRESH_TOKEN);
        }
    }
});

export const {
    setIsOpenAuthModal,
    openAuthModalAt,
    setUserData,
    setToken,
    setRefreshToken,
    logout
} = AuthModalSlice.actions;

const persistConfig = {
    key: 'Auth',
    storage: storage
};

export default persistReducer(persistConfig, AuthModalSlice.reducer);
